/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Stack, useStyleConfig, Text} from '@chakra-ui/react'
import Checkbox from '../../../components/checkbox'
import {
    ADD_FILTER,
    REMOVE_FILTER,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const CheckboxRefinements = ({filter, toggleFilter, selectedFilters, onClose}) => {
    const {formatMessage} = useIntl()
    const styles = useStyleConfig('RadioRefinements')
    const isColorRef = filter?.attributeId === 'c_hexColour'

    const [focusedIdx, setFocusedIdx] = React.useState(0)
    const itemRefs = React.useRef([])

    const handleKeyDown = (e, idx, handleToggle) => {
        if (e.key === 'Tab') {
            e.preventDefault()
            const next = (idx + 1) % filter.values.length
            setFocusedIdx(next)
            itemRefs.current[next]?.focus()
            if (idx + 1 === filter.values.length) {
                onClose()
            }
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const next = (idx + 1) % filter.values.length
            setFocusedIdx(next)
            itemRefs.current[next]?.focus()
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const prev = (idx - 1 + filter.values.length) % filter.values.length
            setFocusedIdx(prev)
            itemRefs.current[prev]?.focus()
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
        }
    }
    return (
        <Stack {...styles?.stack} spacing={1}>
            {filter?.values?.map((value, idx) => {
                const isChecked = selectedFilters.includes(value.value)
                const isDisabled = value.hitCount === 0 && !isChecked
                const colorValues = (value?.presentationId || '')
                    .split(',')
                    .map((color) => color.trim().toLowerCase())
                    .filter(Boolean)
                    .map((color) => (color.startsWith('#') ? color : `#${color}`))
                const colorToken = colorValues?.[0]
                const swatchColor =
                    isColorRef && colorToken
                        ? colorToken.startsWith('#')
                            ? colorToken
                            : `#${colorToken}`
                        : undefined
                const checkColor =
                    isColorRef && swatchColor
                        ? colorValues.includes('#ffffff') || colorValues.includes('#fafafa')
                            ? 'black'
                            : 'white'
                        : undefined

                const handleToggle = () =>
                    toggleFilter(
                        value,
                        filter.attributeId,
                        filter.label,
                        isChecked,
                        true,
                        'standard',
                    )

                return (
                    <Box
                        as="div"
                        key={idx}
                        data-refinement-value={`${filter.attributeId}:${value.value}`}
                        ref={(el) => (itemRefs.current[idx] = el)}
                        tabIndex={focusedIdx === idx ? 0 : -1}
                        onFocus={() => setFocusedIdx(idx)}
                        onKeyDown={(e) => handleKeyDown(e, idx, handleToggle)}
                        role="menuitem"
                        onClick={!isDisabled ? handleToggle : undefined}
                        {...styles?.stackItem}
                        // opacity={isDisabled ? 0.4 : 1}
                        pointerEvents={isDisabled ? 'none' : undefined}
                        _focusVisible={{boxShadow: 'outline', bg: 'none'}}
                        _hover={{bg: 'none'}}
                        _focus={{bg: 'none'}}
                    >
                        <Checkbox
                            isChecked={isChecked}
                            onChange={handleToggle}
                            color={checkColor}
                            aria-label={formatMessage(
                                isChecked ? REMOVE_FILTER : ADD_FILTER,
                                value,
                            )}
                            pointerEvents="none"
                            sx={
                                swatchColor
                                    ? {
                                          '.chakra-checkbox__control': {
                                              bg: swatchColor,
                                              borderColor: swatchColor,
                                              _checked: {
                                                  bg: swatchColor,
                                                  borderColor: swatchColor,
                                              },
                                          },
                                      }
                                    : undefined
                            }
                        />
                        <Text {...styles?.filterLabel}>{value.label}</Text>
                        <Text {...styles?.filterHit}>
                            {formatMessage(
                                {
                                    id: 'checkboxRefinements.hitCount',
                                    defaultMessage: '({checkboxHitCount})',
                                },
                                {checkboxHitCount: value.hitCount},
                            )}
                        </Text>
                    </Box>
                )
            })}
        </Stack>
    )
}

CheckboxRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array,
    onClose: PropTypes.func,
}

export default CheckboxRefinements
